/**
 * @fileoverview Module for `src/lib/hooks/useLocalStorageState.js`.
 */

/**
 * useLocalStorageState Hook
 *
 * Provides React state that persists to localStorage. Useful for preserving
 * user preferences or session data across page reloads.
 *
 * @module lib/hooks/useLocalStorageState
 */

import { useEffect, useState } from 'react';

/**
 * Stores a value in localStorage as JSON.
 *
 * @param {string} key - localStorage key
 * @param {*} value - Value to store (will be JSON stringified)
 */
function setLocalStorageState(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * React hook that syncs state with localStorage.
 *
 * On initial render, reads the value from localStorage (or uses defaultValue).
 * On subsequent updates, writes changes back to localStorage.
 *
 * @param {string} key - localStorage key to sync with
 * @param {*} defaultValue - Default value if key doesn't exist in localStorage
 * @returns {[*, Function]} Tuple of [currentValue, setValue] like useState
 */
function useLocalStorageState(key, defaultValue) {
  const [value, setValue] = useState(
    () => {
      const localStorageValue = localStorage.getItem(key);
      // TODO: update this at some point, as it's just like this to transition pictureUrl usage
      try {
        return JSON.parse(localStorageValue) || defaultValue;
      } catch {
        return localStorageValue || defaultValue;
      }
    },
  );
  useEffect(
    () => { setLocalStorageState(key, value); },
    [key, value],
  );
  return [value, setValue];
}

export default useLocalStorageState;
export { setLocalStorageState };
